import { useState, useRef, useEffect } from "react";
import { CircleFlag } from "react-circle-flags";
import countries from "world-countries";

type CustomPhoneInputProps = {
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
  name?: string;
  error?: string;
};

type Country = {
  name: string;
  cca2: string;
  callingCode: string;
};

const allCountries: Country[] = countries.map((c) => ({
  name: c.name.common,
  cca2: c.cca2.toLowerCase(),
  callingCode: c.idd.root ? `${c.idd.root}${c.idd.suffixes?.[0] || ""}` : "",
}));

export default function CustomPhoneInput({
  label,
  value,
  onChange,
  name,
  error,
}: CustomPhoneInputProps) {
  const [selectedCountry, setSelectedCountry] = useState<Country>(
    allCountries.find((c) => c.cca2 === "et")!
  );
  const [localPhone, setLocalPhone] = useState(value || "");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredCountries = allCountries.filter(
    (c) =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.callingCode.includes(searchTerm)
  );

  const handleSelectCountry = (country: Country) => {
    setSelectedCountry(country);
    setDropdownOpen(false);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalPhone(newValue);
    onChange?.(`${selectedCountry.callingCode}${newValue}`);
  };

  return (
    <div className="w-full">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label ? label : "Primary Phone number"}{" "}
        <span className="text-red-500">*</span>
      </label>

      <div className="relative">
        {/* Input wrapper */}
        <div className="flex items-center relative border border-gray-300 rounded-lg p-1 focus-within:border-blue-500 focus-within:ring-2 h-11 focus-within:ring-blue-100 transition-all duration-200 bg-white">
          {/* Country selector */}
          <div
            className="flex items-center space-x-2 bg-[#F9F9F9] h-8 rounded-md px-2 cursor-pointer"
            onClick={() => setDropdownOpen((prev) => !prev)}
          >
            <div className="w-5 h-5 rounded-full overflow-hidden shadow-sm flex items-center justify-center bg-[#F9F9F9]">
              <CircleFlag countryCode={selectedCountry.cca2} height={20} />
            </div>

            <span className="text-sm text-gray-600 font-medium whitespace-nowrap">
              {selectedCountry.callingCode}
            </span>
          </div>

          {/* Phone input */}
          <input
            type="tel"
            name={name}
            value={localPhone}
            onChange={handlePhoneChange}
            placeholder="Enter phone number"
            className="flex-1 ml-2 border-none focus:outline-none focus:ring-0 px-0 text-sm placeholder-gray-400 bg-transparent rounded-md"
          />
        </div>

        {/* Dropdown */}
        {dropdownOpen && (
          <div
            ref={dropdownRef}
            className="absolute mt-1 w-full max-h-64 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg z-50"
          >
            {/* Search input */}
            <div className="p-3 border-b border-gray-100">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search country"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
              />
            </div>

            {/* Country list */}
            <div className="max-h-48 overflow-y-auto">
              {filteredCountries.map((c) => (
                <div
                  key={c.cca2}
                  className="flex items-center px-4 py-2 hover:bg-blue-50 cursor-pointer transition-colors"
                  onClick={() => handleSelectCountry(c)}
                >
                  <div className="w-6 h-6 rounded-full overflow-hidden border border-gray-200 shadow-sm flex items-center justify-center mr-3">
                    <CircleFlag countryCode={c.cca2} height={16} />
                  </div>
                  <span className="flex-1 text-sm text-gray-700">{c.name}</span>
                  <span className="text-xs text-gray-500 font-medium">
                    +{c.callingCode}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && <p className="text-red-500 text-sm mt-1 ml-1">{error}</p>}
      </div>
    </div>
  );
}
